import { CircleStackIcon, ExclamationTriangleIcon, InformationCircleIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "src/async/apiUtils";
import Section from "src/components/Shared/Containers/Section";
import Badge from "src/components/ServerMonitoring/Badge";
import StatTile from "src/components/ServerMonitoring/StatTile";
import { classNames } from "src/helpers/classNames";
import { formatDateTime, formatRelative } from "src/helpers/serverMonitorHelper";

export const databaseStatusStyles = {
  online: { label: "Online", dot: "bg-green-500", badge: "bg-green-100 text-green-700" },
  creating: { label: "Creating", dot: "bg-blue-500", badge: "bg-blue-100 text-blue-700" },
  resizing: { label: "Resizing", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-700" },
  migrating: { label: "Migrating", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-700" },
  forking: { label: "Forking", dot: "bg-amber-500", badge: "bg-amber-100 text-amber-700" },
  not_found: { label: "Not found", dot: "bg-red-500", badge: "bg-red-100 text-red-700" },
  unknown: { label: "Unknown", dot: "bg-gray-400", badge: "bg-gray-100 text-gray-600" },
};

const Item = ({ label, children }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <div className="text-sm font-semibold text-gray-900">{children ?? "—"}</div>
  </div>
);

const formatSize = (mb) => (mb == null ? "—" : mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`);

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const DatabaseCard = ({ database, stats }) => {
  const [events, setEvents] = useState(null);
  const [eventsError, setEventsError] = useState(null);
  const [eventsUnsupported, setEventsUnsupported] = useState(false);
  const style = databaseStatusStyles[database.status] || databaseStatusStyles.unknown;

  const loadEvents = useCallback(async () => {
    if (!database.id) return;
    try {
      setEventsError(null);
      const res = await apiRequest("get", "/server-monitor/databases/:database_id/events", { params: { database_id: database.id } });
      setEvents(res.data.data || []);
      setEventsUnsupported(!!res.data.meta?.unsupported);
    } catch (err) {
      setEventsError(err.message);
    }
  }, [database.id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  if (database.status === "not_found") {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        <b>{database.name}</b> is attached to the app but was not found among the account&apos;s database clusters.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-gray-100 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <CircleStackIcon className="h-5 w-5 text-gray-400" />
        <span className="font-semibold text-gray-900">{database.name}</span>
        <Badge
          label={style.label}
          className={style.badge}
          dot={style.dot}
        />
      </div>

      {stats && !stats.error && (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="CPU"
            value={stats.cpu_percent != null ? `${stats.cpu_percent}%` : "Measuring…"}
            percent={stats.cpu_percent}
            detail={`${stats.cores} vCPU`}
          />
          <StatTile
            label="Memory"
            value={stats.memory_percent != null ? `${stats.memory_percent}%` : "—"}
            percent={stats.memory_percent}
            detail={`${formatSize(stats.memory_mb)} of ${formatSize(stats.memory_total_mb)}`}
          />
          <StatTile
            label="Disk"
            value={stats.disk_percent != null ? `${stats.disk_percent}%` : "—"}
            percent={stats.disk_percent}
            detail={`${formatSize(stats.disk_used_mb)} of ${formatSize(stats.disk_total_mb)}`}
            warnAt={75}
            badAt={90}
          />
          <StatTile
            label="Connections"
            value={stats.connections_current.toLocaleString()}
            percent={stats.connections_available ? Math.round((stats.connections_current / (stats.connections_current + stats.connections_available)) * 1000) / 10 : null}
            detail={`${stats.connections_available.toLocaleString()} more available`}
          />
        </div>
      )}
      {stats?.error && <p className="mt-4 text-sm text-amber-700">{stats.error}</p>}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Item label="Engine">
          {database.engine === "mongodb" ? "MongoDB" : capitalize(database.engine)} {database.version}
        </Item>
        <Item label="Nodes">
          {database.num_nodes}
          <div className="text-xs font-normal text-gray-400">{database.num_nodes > 1 ? "Primary + standby" : "No standby node"}</div>
        </Item>
        <Item label="Size">
          <span className="break-all">{database.size}</span>
          {database.storage_size_mib ? <div className="text-xs font-normal text-gray-400">+{Math.round(database.storage_size_mib / 1024)} GB storage</div> : null}
        </Item>
        <Item label="Region">{database.region?.toUpperCase()}</Item>
      </div>

      <div className="mt-4 space-y-2">
        {database.maintenance?.pending && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <WrenchScrewdriverIcon className="h-5 w-5 flex-shrink-0" />
            <div>
              <b>Maintenance pending</b> - it runs in the next window ({capitalize(database.maintenance.day)} {database.maintenance.hour} UTC). The database may be briefly unavailable
              {database.num_nodes > 1 ? " while it switches to the standby node" : ", which means the SaaS Portal is briefly down"}.
              {database.maintenance.description?.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-xs">
                  {database.maintenance.description.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        {!database.maintenance?.pending && database.maintenance?.day && (
          <p className="text-xs text-gray-500">
            Maintenance window: {capitalize(database.maintenance.day)} {database.maintenance.hour} UTC. No maintenance pending.
          </p>
        )}
        {database.version_eol_soon && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <span>
              This database version reaches end of life on <b>{formatDateTime(database.version_end_of_life, "MMM D, YYYY")}</b>. Plan an upgrade in DigitalOcean.
            </span>
          </div>
        )}
        {database.num_nodes === 1 && (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>Only 1 node: if the database node fails or restarts for maintenance, the SaaS Portal cannot reach its data until it is back. A standby node takes over automatically.</span>
          </div>
        )}
      </div>

      {/* DigitalOcean has no events log for some engines (e.g. MongoDB) - the block is hidden then. */}
      <div className={classNames("mt-4", eventsUnsupported && "hidden")}>
        <h4 className="text-sm font-semibold text-gray-900">Recent events</h4>
        {eventsError && <p className="mt-1 text-sm text-amber-700">{eventsError}</p>}
        {events && events.length === 0 && <p className="mt-1 text-sm text-gray-400">No recent events.</p>}
        {events?.length > 0 && (
          <ul className="mt-2 divide-y divide-gray-50 rounded-md border border-gray-100">
            {events.map((event) => (
              <li
                key={event.id || `${event.type}-${event.at}`}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className={classNames(event.type === "cluster_master_promotion" || event.type === "cluster_poweroff" ? "font-medium text-amber-800" : "text-gray-700")}>{event.label}</span>
                <span className="whitespace-nowrap text-xs text-gray-400">{event.at ? formatDateTime(event.at, "MMM D, YYYY h:mm A") : "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Managed databases attached to the SaaS app (status, version, nodes, maintenance, events).
const DatabaseHealth = ({ status }) => {
  const database = status?.database;
  if (!status?.configured || !database || database.attached?.length === 0) return null;

  return (
    <Section shadow>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-bold text-gray-900">Database</h2>
        {database.checked_at && <span className="text-xs text-gray-400">Checked {formatRelative(database.checked_at)} · refreshed every 5 minutes</span>}
      </div>

      {database.error ? (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            {database.message || "Database status is not available right now."} Attached: {database.attached.join(", ")}.
          </span>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {database.databases.map((item) => (
            <DatabaseCard
              key={item.id || item.name}
              database={item}
              stats={item.stats_available ? database.stats : null}
            />
          ))}
        </div>
      )}
    </Section>
  );
};

export default DatabaseHealth;
